import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { DollarSign, TrendingUp, Calendar, AlertCircle, Save, Loader2 } from "lucide-react";
import { addDays, addWeeks, addMonths, format, differenceInDays, differenceInMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export default function PaymentPlanV2({ 
  totalValue, 
  upsellCredits = 0,
  dealId, 
  onPaymentsSaved, 
  onScheduleChange, 
  disabled = false,
  initialConfig,
  onConfigChange
}) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  
  // Effective total is the amount to be paid (total minus upsell credits)
  const effectiveTotal = totalValue - upsellCredits;
  
  // Initialize with initialConfig values or defaults
  const [depositAmount, setDepositAmount] = useState(() => 
    initialConfig?.depositAmount ?? effectiveTotal * 0.3
  );
  const [depositSplit, setDepositSplit] = useState(() => 
    initialConfig?.depositSplit ?? {
      number_of_payments: 1,
      frequency: "weekly",
      start_date: new Date().toISOString().split('T')[0]
    }
  );
  
  const [installmentSplit, setInstallmentSplit] = useState(() => 
    initialConfig?.installmentSplit ?? {
      number_of_payments: 1,
      frequency: "monthly",
      start_date: new Date().toISOString().split('T')[0]
    }
  );

  // Custom dates and amounts for manual selection
  const [depositCustomDates, setDepositCustomDates] = useState(() => 
    initialConfig?.depositCustomDates ?? []
  );
  const [installmentCustomDates, setInstallmentCustomDates] = useState(() => 
    initialConfig?.installmentCustomDates ?? []
  );
  const [depositCustomAmounts, setDepositCustomAmounts] = useState(() => 
    initialConfig?.depositCustomAmounts ?? []
  );
  const [installmentCustomAmounts, setInstallmentCustomAmounts] = useState(() => 
    initialConfig?.installmentCustomAmounts ?? []
  );

  // Track if initialConfig has been applied (by its stringified value)
  const appliedConfigRef = useRef(null);

  // Sync states when initialConfig changes (async deal data)
  useEffect(() => {
    if (!initialConfig) return;
    
    // Create a stable key to track if this config was already applied
    const configKey = JSON.stringify(initialConfig);
    if (appliedConfigRef.current === configKey) return;
    
    appliedConfigRef.current = configKey;
    
    if (initialConfig.depositAmount !== undefined) {
      setDepositAmount(initialConfig.depositAmount);
    }
    if (initialConfig.depositSplit) {
      setDepositSplit(initialConfig.depositSplit);
    }
    if (initialConfig.installmentSplit) {
      setInstallmentSplit(initialConfig.installmentSplit);
    }
    if (initialConfig.depositCustomDates?.length) {
      setDepositCustomDates(initialConfig.depositCustomDates);
    }
    if (initialConfig.depositCustomAmounts?.length) {
      setDepositCustomAmounts(initialConfig.depositCustomAmounts);
    }
    if (initialConfig.installmentCustomDates?.length) {
      setInstallmentCustomDates(initialConfig.installmentCustomDates);
    }
    if (initialConfig.installmentCustomAmounts?.length) {
      setInstallmentCustomAmounts(initialConfig.installmentCustomAmounts);
    }
  }, [initialConfig]);

  // Calculate derived values first (before useEffects that depend on them)
  const installmentAmount = effectiveTotal - depositAmount;
  const depositPerPayment = depositAmount / (depositSplit.number_of_payments || 1);

  // Initialize custom dates and amounts when switching to custom or changing number of payments
  useEffect(() => {
    // Skip if we already have custom dates (loaded from config or user-set)
    if (depositCustomDates.length === depositSplit.number_of_payments) return;
    
    if (depositSplit.frequency === "custom") {
      const today = new Date();
      const dates = Array.from({ length: depositSplit.number_of_payments }, (_, i) => {
        const date = addDays(today, i);
        return format(date, 'yyyy-MM-dd');
      });
      setDepositCustomDates(dates);
      
      // Initialize amounts evenly distributed
      const perPayment = depositAmount / depositSplit.number_of_payments;
      const amounts = Array.from({ length: depositSplit.number_of_payments }, () => Math.round(perPayment * 100) / 100);
      setDepositCustomAmounts(amounts);
    }
  }, [depositSplit.frequency, depositSplit.number_of_payments, depositAmount, depositCustomDates.length]);

  useEffect(() => {
    // Skip if we already have custom dates (loaded from config or user-set)
    if (installmentCustomDates.length === installmentSplit.number_of_payments) return;
    
    if (installmentSplit.frequency === "custom") {
      const today = new Date();
      const dates = Array.from({ length: installmentSplit.number_of_payments }, (_, i) => {
        const date = addDays(today, i);
        return format(date, 'yyyy-MM-dd');
      });
      setInstallmentCustomDates(dates);
      
      // Initialize amounts evenly distributed
      const perPayment = installmentAmount / installmentSplit.number_of_payments;
      const amounts = Array.from({ length: installmentSplit.number_of_payments }, () => Math.round(perPayment * 100) / 100);
      setInstallmentCustomAmounts(amounts);
    }
  }, [installmentSplit.frequency, installmentSplit.number_of_payments, installmentAmount, installmentCustomDates.length]);

  // Manual save config function
  const handleSaveConfig = async () => {
    if (!onConfigChange || disabled) return;
    
    setIsSavingConfig(true);
    try {
      const config = {
        depositAmount,
        depositSplit,
        installmentSplit,
        depositCustomDates,
        depositCustomAmounts,
        installmentCustomDates,
        installmentCustomAmounts
      };
      
      await onConfigChange(config);
      toast.success(t('paymentPlan.configSaved') || 'Plano de pagamento salvo!');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(t('common.error') || 'Erro ao salvar');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const updateDepositCustomDate = (index, value) => {
    const newDates = [...depositCustomDates];
    newDates[index] = value;
    setDepositCustomDates(newDates);
  };

  const updateInstallmentCustomDate = (index, value) => {
    const newDates = [...installmentCustomDates];
    newDates[index] = value;
    setInstallmentCustomDates(newDates);
  };

  const updateDepositCustomAmount = (index, value) => {
    const newAmounts = [...depositCustomAmounts];
    newAmounts[index] = Number(value) || 0;
    setDepositCustomAmounts(newAmounts);
  };

  const updateInstallmentCustomAmount = (index, value) => {
    const newAmounts = [...installmentCustomAmounts];
    newAmounts[index] = Number(value) || 0;
    setInstallmentCustomAmounts(newAmounts);
  };

  // Calculate custom amounts totals and differences for validation
  const depositCustomTotal = depositCustomAmounts.reduce((sum, amt) => sum + (amt || 0), 0);
  const depositDifference = depositAmount - depositCustomTotal;
  
  const installmentCustomTotal = installmentCustomAmounts.reduce((sum, amt) => sum + (amt || 0), 0);
  const installmentDifference = installmentAmount - installmentCustomTotal;
  
  // Round UP to nearest $5 so the last payment is always lower
  const roundToNearestFive = (amount) => {
    // Ensure amount is a number and handle potential NaN or infinity
    if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount)) {
      return 0; // Or handle error appropriately
    }
    return Math.ceil(amount / 5) * 5;
  };
  
  const calculateInstallmentPayments = () => {
    const numPayments = installmentSplit.number_of_payments || 1;
    
    if (numPayments === 0) { // Handle case where number of payments is 0 to avoid division by zero
      return {
        regularAmount: 0,
        lastPayment: 0
      };
    }

    if (numPayments === 1) {
      return {
        regularAmount: installmentAmount,
        lastPayment: installmentAmount
      };
    }
    
    const rawAmount = installmentAmount / numPayments;
    const roundedAmount = roundToNearestFive(rawAmount);
    
    // Calculate what the last payment should be
    const totalOfRegularPayments = roundedAmount * (numPayments - 1);
    const lastPayment = installmentAmount - totalOfRegularPayments;
    
    return {
      regularAmount: roundedAmount,
      lastPayment: lastPayment
    };
  };
  
  const installmentPayments = calculateInstallmentPayments();

  const generateDepositSchedule = () => {
    const schedule = [];
    
    // Handle custom dates and amounts
    if (depositSplit.frequency === "custom") {
      for (let i = 0; i < depositSplit.number_of_payments; i++) {
        const customDate = depositCustomDates[i];
        const customAmount = depositCustomAmounts[i] || depositPerPayment;
        if (customDate) {
          const [year, month, day] = customDate.split('-').map(Number);
          const dueDate = new Date(year, month - 1, day);
          schedule.push({
            number: i + 1,
            amount: customAmount,
            due_date: dueDate,
            type: "deposit"
          });
        }
      }
      return schedule;
    }
    
    // Fix timezone issue by creating date from parts
    const [year, month, day] = depositSplit.start_date.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);
    
    for (let i = 0; i < depositSplit.number_of_payments; i++) {
      let dueDate;
      if (i === 0) {
        dueDate = startDate;
      } else {
        switch (depositSplit.frequency) {
          case "weekly":
            dueDate = addWeeks(startDate, i);
            break;
          case "biweekly":
            dueDate = addWeeks(startDate, i * 2);
            break;
          case "monthly":
            dueDate = addMonths(startDate, i);
            break;
          default:
            dueDate = addWeeks(startDate, i);
        }
      }
      
      schedule.push({
        number: i + 1,
        amount: depositPerPayment,
        due_date: dueDate,
        type: "deposit"
      });
    }
    
    return schedule;
  };

  const generateInstallmentSchedule = () => {
    const schedule = [];
    const numPayments = installmentSplit.number_of_payments || 1;
    
    // Handle custom dates and amounts
    if (installmentSplit.frequency === "custom") {
      for (let i = 0; i < numPayments; i++) {
        const customDate = installmentCustomDates[i];
        const customAmount = installmentCustomAmounts[i] || installmentPayments.regularAmount;
        if (customDate) {
          const [year, month, day] = customDate.split('-').map(Number);
          const dueDate = new Date(year, month - 1, day);
          schedule.push({
            number: i + 1,
            amount: customAmount,
            due_date: dueDate,
            type: "installment"
          });
        }
      }
      return schedule;
    }
    
    // Fix timezone issue by creating date from parts
    const [year, month, day] = installmentSplit.start_date.split('-').map(Number);
    const startDate = new Date(year, month - 1, day);
    
    for (let i = 0; i < numPayments; i++) {
      let dueDate;
      if (i === 0) {
        dueDate = startDate;
      } else {
        switch (installmentSplit.frequency) {
          case "weekly":
            dueDate = addWeeks(startDate, i);
            break;
          case "biweekly":
            dueDate = addWeeks(startDate, i * 2);
            break;
          case "monthly":
            dueDate = addMonths(startDate, i);
            break;
          default:
            dueDate = addMonths(startDate, i);
        }
      }
      
      const amount = (i === numPayments - 1) 
        ? installmentPayments.lastPayment 
        : installmentPayments.regularAmount;
      
      schedule.push({
        number: i + 1,
        amount: amount,
        due_date: dueDate,
        type: "installment"
      });
    }
    
    return schedule;
  };

  const depositSchedule = generateDepositSchedule();
  const installmentSchedule = generateInstallmentSchedule();
  const fullSchedule = [...depositSchedule, ...installmentSchedule].sort((a, b) => 
    new Date(a.due_date) - new Date(b.due_date)
  );

  // Notify parent when schedule changes
  useEffect(() => {
    if (onScheduleChange) {
      onScheduleChange(fullSchedule);
    }
  }, [JSON.stringify(fullSchedule)]);

  const calculatePaymentTerm = () => {
    if (fullSchedule.length === 0) return null;
    
    const firstPayment = fullSchedule[0].due_date;
    const lastPayment = fullSchedule[fullSchedule.length - 1].due_date;
    
    // Convert to Date objects to ensure correct calculations if they are strings
    const firstDate = new Date(firstPayment);
    const lastDate = new Date(lastPayment);

    const days = differenceInDays(lastDate, firstDate);
    
    if (days === 0) return t('paymentPlan.sameDay');
    
    const months = differenceInMonths(lastDate, firstDate);
    
    let termString = "";
    
    if (months > 0) {
      termString += `${months} ${months > 1 ? t('paymentPlan.months') : t('paymentPlan.month')}`;
      // Calculate remaining days after full months
      const dateAfterMonths = addMonths(firstDate, months);
      const remainingDays = differenceInDays(lastDate, dateAfterMonths);

      if (remainingDays > 0) {
        const remainingWeeks = Math.round(remainingDays / 7);
        if (remainingWeeks > 0) {
          termString += ` ${t('paymentPlan.and')} ${remainingWeeks} ${remainingWeeks > 1 ? t('paymentPlan.weeks') : t('paymentPlan.week')}`;
        } else if (remainingDays > 0) {
          termString += ` ${t('paymentPlan.and')} ${remainingDays} ${remainingDays > 1 ? t('paymentPlan.days') : t('paymentPlan.day')}`;
        }
      }
    } else { // Less than a month, just show days or weeks
      const weeks = Math.round(days / 7);
      if (weeks > 0) {
        termString += `${weeks} ${weeks > 1 ? t('paymentPlan.weeks') : t('paymentPlan.week')}`;
        const remainingDays = days - (weeks * 7);
        if (remainingDays > 0) {
          termString += ` ${t('paymentPlan.and')} ${remainingDays} ${remainingDays > 1 ? t('paymentPlan.days') : t('paymentPlan.day')}`;
        }
      } else {
        termString += `${days} ${days > 1 ? t('paymentPlan.days') : t('paymentPlan.day')}`;
      }
    }
    
    return termString.trim();
  };

  const checkDepositWarning = () => {
    const minimumDeposit = effectiveTotal * 0.2; // 20% minimum
    
    if (depositSchedule.length === 0) {
      // If there are no deposit payments, but a deposit amount is set, it means depositAmount was 0 or number_of_payments was 0.
      // In this specific scenario, we'll check if the overall deposit is less than the minimum.
      // If totalValue is 0, minimumDeposit will be 0, so this won't trigger unless totalValue > 0.
      if (depositAmount < minimumDeposit) {
        return {
          show: true,
          message: `Deposit should be at least 20% ($${minimumDeposit.toLocaleString(undefined, { minimumFractionDigits: 2 })}) for a good deal structure.`
        };
      }
      return { show: false, message: "" }; // No deposit payments, but depositAmount is sufficient or totalValue is 0.
    }
    
    // Calculate how much deposit is received within first 30 days
    const firstDepositDate = new Date(depositSchedule[0].due_date);
    const cutoffDate = addDays(firstDepositDate, 30);
    
    const depositWithin30Days = depositSchedule
      .filter(payment => new Date(payment.due_date) <= cutoffDate)
      .reduce((sum, payment) => sum + payment.amount, 0);
    
    // Check if at least 20% is received within 30 days
    if (depositWithin30Days < minimumDeposit) {
      return {
        show: true,
        message: `At least 20% ($${minimumDeposit.toLocaleString(undefined, { minimumFractionDigits: 2 })}) should be received within 30 days. Currently: $${depositWithin30Days.toLocaleString(undefined, { minimumFractionDigits: 2 })} received in first 30 days.`
      };
    }
    
    return { show: false, message: "" };
  };

  const paymentTerm = calculatePaymentTerm();
  const depositWarning = checkDepositWarning();

  const handleSavePaymentPlan = async () => {
    if (!dealId) {
      toast.error('Deal ID is required');
      return;
    }

    if (fullSchedule.length === 0) {
      toast.error('No payments to save');
      return;
    }

    setIsSaving(true);
    try {
      
      // Create all payments from the schedule
      const paymentsToCreate = fullSchedule.map((payment, index) => ({
        deal_id: dealId,
        payment_number: `Payment ${index + 1}`,
        installment_number: index + 1,
        payment_type: payment.type === 'deposit' ? 'down_payment' : payment.type,
        description: `${payment.type === 'deposit' ? 'Deposit' : 'Installment'} payment ${payment.number}`,
        amount: payment.amount,
        due_date: format(payment.due_date, 'yyyy-MM-dd'),
        status: 'pending',
        paid_amount: 0,
        balance: payment.amount,
      }));

      const { error } = await supabase
        .from('payments')
        .insert(paymentsToCreate);

      if (error) throw error;

      toast.success(`${paymentsToCreate.length} payments created successfully`);
      
      // Call the callback if provided
      if (onPaymentsSaved) {
        onPaymentsSaved();
      }
    } catch (error) {
      console.error('Error saving payment plan:', error);
      toast.error('Failed to save payment plan');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate actual payable amount
  const actualPayableAmount = totalValue - upsellCredits;

  return (
    <div className="space-y-3">
      {/* Total Amount Display */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="p-3">
          <div className="text-center">
            <p className="text-sm text-slate-600 mb-1">{t('paymentPlan.totalDealValue')}</p>
            <p className="text-3xl font-bold text-blue-600">
              ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            {paymentTerm && (
              <div className="mt-2 pt-2 border-t border-blue-200">
                <p className="text-xs text-slate-600 mb-1">{t('paymentPlan.paymentTermLength')}</p>
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <p className="text-lg font-semibold text-blue-700">
                    {paymentTerm}
                  </p>
                </div>
                {fullSchedule.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {format(fullSchedule[0].due_date, "d 'de' MMM 'de' yyyy", { locale: ptBR })} - {format(fullSchedule[fullSchedule.length - 1].due_date, "d 'de' MMM 'de' yyyy", { locale: ptBR })}
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upsell Credits Card - Only show if there are credits */}
      {upsellCredits > 0 && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-purple-900">{t('paymentPlan.upsellCredits') || 'Créditos de Upsell'}</p>
                <p className="text-sm text-purple-600">{t('paymentPlan.transferredFromPrevious') || 'Pagamentos transferidos do contrato anterior'}</p>
              </div>
              <p className="text-lg font-bold text-purple-700">
                -${upsellCredits.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="mt-2 pt-2 border-t border-purple-200 text-center">
              <p className="text-sm text-slate-600">{t('paymentPlan.amountToPay') || 'Saldo a Pagar'}</p>
              <p className="text-xl font-bold text-emerald-600">
                ${actualPayableAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-3">
        {/* Deposit Section */}
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="border-b border-emerald-100">
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <DollarSign className="w-5 h-5" />
              {t('paymentPlan.deposit')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            <div className="space-y-1">
              <Label>{t('paymentPlan.depositAmount')}</Label>
              <Input
                type="number"
                step="0.01"
                value={depositAmount}
                onChange={(e) => setDepositAmount(Number(e.target.value) || 0)}
                className="text-lg font-bold text-emerald-700 border-emerald-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                disabled={disabled}
              />
              <input
                type="range"
                min="0"
                max={totalValue}
                step="50"
                value={depositAmount}
                onChange={(e) => setDepositAmount(Number(e.target.value))}
                className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                disabled={disabled}
              />
              <p className="text-xs text-slate-600 text-center">
                {((depositAmount / totalValue) * 100).toFixed(1)}% {t('paymentPlan.ofTotal')}
              </p>
            </div>

            <div className="pt-2 border-t border-emerald-200">
              <Label className="text-sm mb-1 block">{t('paymentPlan.splitDepositInto')}</Label>
              
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">{t('paymentPlan.numberOfPayments')}</Label>
                  <Select
                    value={String(depositSplit.number_of_payments)}
                    onValueChange={(value) => setDepositSplit({
                      ...depositSplit, 
                      number_of_payments: Number(value)
                    })}
                    disabled={disabled}
                  >
                    <SelectTrigger className="border-emerald-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => (
                        <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">{t('paymentPlan.frequency')}</Label>
                  <Select
                    value={depositSplit.frequency}
                    onValueChange={(value) => setDepositSplit({...depositSplit, frequency: value})}
                    disabled={disabled}
                  >
                    <SelectTrigger className="border-emerald-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">{t('paymentPlan.weekly')}</SelectItem>
                      <SelectItem value="biweekly">{t('paymentPlan.biweekly')}</SelectItem>
                      <SelectItem value="monthly">{t('paymentPlan.monthly')}</SelectItem>
                      <SelectItem value="custom">{t('paymentPlan.customDates')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {depositSplit.frequency === "custom" ? (
                  <div className="space-y-2">
                    <Label className="text-xs">{t('paymentPlan.paymentDatesAndAmounts')}</Label>
                    {Array.from({ length: depositSplit.number_of_payments }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-8">#{i + 1}</span>
                        <DatePickerInput
                          className="flex-1"
                          value={depositCustomDates[i] || null}
                          onChange={(date) => date && updateDepositCustomDate(i, format(date, 'yyyy-MM-dd'))}
                          disabled={disabled}
                        />
                        <div className="relative w-28">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={depositCustomAmounts[i] || 0}
                            onChange={(e) => updateDepositCustomAmount(i, e.target.value)}
                            className="pl-7 border-emerald-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            disabled={disabled}
                          />
                        </div>
                      </div>
                    ))}
                    {Math.abs(depositDifference) > 0.01 && (
                      <div className={cn(
                        "p-2 rounded text-sm",
                        depositDifference > 0 
                          ? "bg-amber-100 text-amber-800" 
                          : "bg-red-100 text-red-800"
                      )}>
                        {depositDifference > 0 
                          ? t('paymentPlan.missingAmount', { amount: `$${depositDifference.toFixed(2)}` })
                          : t('paymentPlan.exceededAmount', { amount: `$${Math.abs(depositDifference).toFixed(2)}` })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">{t('paymentPlan.startDate')}</Label>
                    <DatePickerInput
                      value={depositSplit.start_date || null}
                      onChange={(date) => date && setDepositSplit({...depositSplit, start_date: format(date, 'yyyy-MM-dd')})}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>

              {depositAmount > 0 && (
                <div className="mt-2 p-2 bg-emerald-100 rounded-lg">
                  <p className="text-sm font-semibold text-emerald-900">
                    ${depositPerPayment.toLocaleString(undefined, { minimumFractionDigits: 2 })} 
                    <span className="text-xs font-normal text-emerald-700">
                      {' '}{t('paymentPlan.per')} {depositSplit.frequency === 'custom' ? t('paymentPlan.customScheduled') : t(`paymentPlan.${depositSplit.frequency}`)} {t('paymentPlan.payment')}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Installments Section */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="border-b border-blue-100">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <TrendingUp className="w-5 h-5" />
              {t('paymentPlan.installments')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            <div className="space-y-1">
              <Label>{t('paymentPlan.installmentAmount')}</Label>
              <div className="text-lg font-bold text-blue-700 p-2 bg-white border border-blue-300 rounded-lg">
                ${installmentAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-slate-600 text-center">
                {((installmentAmount / totalValue) * 100).toFixed(1)}% {t('paymentPlan.ofTotal')} {t('paymentPlan.autoCalculated')}
              </p>
            </div>

            <div className="pt-2 border-t border-blue-200">
              <Label className="text-sm mb-1 block">{t('paymentPlan.splitInstallmentsInto')}</Label>
              
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">{t('paymentPlan.numberOfPayments')}</Label>
                  <Select
                    value={String(installmentSplit.number_of_payments)}
                    onValueChange={(value) => setInstallmentSplit({
                      ...installmentSplit, 
                      number_of_payments: Number(value)
                    })}
                    disabled={disabled}
                  >
                    <SelectTrigger className="border-blue-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 60 }, (_, i) => i + 1).map((num) => (
                        <SelectItem key={num} value={String(num)}>{num}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">{t('paymentPlan.frequency')}</Label>
                  <Select
                    value={installmentSplit.frequency}
                    onValueChange={(value) => setInstallmentSplit({...installmentSplit, frequency: value})}
                    disabled={disabled}
                  >
                    <SelectTrigger className="border-blue-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">{t('paymentPlan.weekly')}</SelectItem>
                      <SelectItem value="biweekly">{t('paymentPlan.biweekly')}</SelectItem>
                      <SelectItem value="monthly">{t('paymentPlan.monthly')}</SelectItem>
                      <SelectItem value="custom">{t('paymentPlan.customDates')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {installmentSplit.frequency === "custom" ? (
                  <div className="space-y-2">
                    <Label className="text-xs">{t('paymentPlan.paymentDatesAndAmounts')}</Label>
                    {Array.from({ length: installmentSplit.number_of_payments }).map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-8">#{i + 1}</span>
                        <DatePickerInput
                          className="flex-1"
                          value={installmentCustomDates[i] || null}
                          onChange={(date) => date && updateInstallmentCustomDate(i, format(date, 'yyyy-MM-dd'))}
                          disabled={disabled}
                        />
                        <div className="relative w-28">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={installmentCustomAmounts[i] || 0}
                            onChange={(e) => updateInstallmentCustomAmount(i, e.target.value)}
                            className="pl-7 border-blue-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            disabled={disabled}
                          />
                        </div>
                      </div>
                    ))}
                    {Math.abs(installmentDifference) > 0.01 && (
                      <div className={cn(
                        "p-2 rounded text-sm",
                        installmentDifference > 0 
                          ? "bg-amber-100 text-amber-800" 
                          : "bg-red-100 text-red-800"
                      )}>
                        {installmentDifference > 0 
                          ? t('paymentPlan.missingAmount', { amount: `$${installmentDifference.toFixed(2)}` })
                          : t('paymentPlan.exceededAmount', { amount: `$${Math.abs(installmentDifference).toFixed(2)}` })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">{t('paymentPlan.startDate')}</Label>
                    <DatePickerInput
                      value={installmentSplit.start_date || null}
                      onChange={(date) => date && setInstallmentSplit({...installmentSplit, start_date: format(date, 'yyyy-MM-dd')})}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>

              {installmentAmount > 0 && (
                <div className="mt-2 space-y-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900">
                      ${installmentPayments.regularAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} 
                      <span className="text-xs font-normal text-blue-700">
                        {' '}{t('paymentPlan.per')} {installmentSplit.frequency === 'custom' ? t('paymentPlan.customScheduled') : t(`paymentPlan.${installmentSplit.frequency}`)} {t('paymentPlan.payment')}
                      </span>
                    </p>
                    {installmentSplit.number_of_payments > 1 && (
                      <p className="text-xs text-blue-600 mt-1">
                        ({installmentSplit.number_of_payments - 1} {t('paymentPlan.payments')})
                      </p>
                    )}
                  </div>
                  {installmentSplit.number_of_payments > 1 && installmentPayments.lastPayment !== installmentPayments.regularAmount && (
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <p className="text-sm font-semibold text-indigo-900">
                        ${installmentPayments.lastPayment.toLocaleString(undefined, { minimumFractionDigits: 2 })} 
                        <span className="text-xs font-normal text-indigo-700">
                          {' '}{t('paymentPlan.finalPayment')}
                        </span>
                      </p>
                      <p className="text-xs text-indigo-600 mt-1">
                        {t('paymentPlan.lowerFinalPayment')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Plan Button */}
      {!disabled && onConfigChange && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSaveConfig}
            disabled={isSavingConfig}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSavingConfig ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('common.saving')}...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('paymentPlan.savePlan')}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Warning Banner */}
      {depositWarning.show && (
        <Card className="border-amber-500 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900 mb-1">{t('paymentPlan.notIdealStructure')}</p>
                <p className="text-sm text-amber-800">{depositWarning.message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Schedule Timeline */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-600" />
            {t('paymentPlan.completePaymentSchedule')} ({fullSchedule.length} {t('paymentPlan.payments')})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {fullSchedule.length > 0 ? (
            <div className="space-y-2">
              {fullSchedule.map((payment, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg border-l-4 ${
                    payment.type === "deposit"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-blue-500 bg-blue-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge
                        className={
                          payment.type === "deposit"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-blue-100 text-blue-700"
                        }
                      >
                        {payment.type === "deposit" ? t('paymentPlan.deposit') : t('paymentPlan.installments')} #{payment.number}
                      </Badge>
                      <p className="text-sm text-slate-600 mt-1">
                        {format(payment.due_date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">
                        ${payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-8">
              {t('paymentPlan.adjustSettings')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
